/**
 * Persistent in-memory cache for grocery list data.
 * Keyed by household_id for list; by list id for single list.
 * Only refetch when cache is invalidated (create/update/delete) or empty.
 */

export interface GroceryItem {
  id: string;
  name: string;
  category: string;
  checked: boolean;
  recipeId?: string;
  addedAt: number;
  /** Order within the list; default to array index when missing (legacy). Sort by position then addedAt. */
  position: number;
  /** For smart aggregation: normalized name for matching */
  normalizedName?: string;
  /** Numeric quantity when parsed from recipe */
  quantity?: number;
  /** Unit (e.g. cups, tbsp, whole) when parsed */
  unit?: string;
  /** Recipe IDs this item was aggregated from */
  sourceRecipes?: string[];
}

export interface GroceryList {
  id: string;
  name: string;
  items: GroceryItem[];
  createdAt: number;
}

interface ListsCacheEntry {
  data: GroceryList[];
  householdId: string;
  timestamp: number;
}

const listsByHousehold: Record<string, ListsCacheEntry> = {};
const listByIdCache: Record<string, GroceryList> = {};
let cacheInvalidatedAt = 0;

export function getCachedLists(householdId: string | null): GroceryList[] | null {
  if (!householdId) return null;
  const cached = listsByHousehold[householdId];
  if (!cached) return null;
  if (cached.timestamp < cacheInvalidatedAt) return null;
  return cached.data;
}

export function setCachedLists(householdId: string, data: GroceryList[]): void {
  if (!householdId) return;
  listsByHousehold[householdId] = { data, householdId, timestamp: Date.now() };
  data.forEach((list) => {
    listByIdCache[list.id] = list;
  });
}

export function getCachedList(listId: string): GroceryList | null {
  return listByIdCache[listId] ?? null;
}

export function setCachedList(list: GroceryList): void {
  listByIdCache[list.id] = list;
}

export function removeCachedList(listId: string): void {
  delete listByIdCache[listId];
  for (const householdId of Object.keys(listsByHousehold)) {
    const entry = listsByHousehold[householdId];
    entry.data = entry.data.filter((l) => l.id !== listId);
  }
}

export function updateCachedListInLists(list: GroceryList): void {
  listByIdCache[list.id] = list;
  for (const householdId of Object.keys(listsByHousehold)) {
    const entry = listsByHousehold[householdId];
    const idx = entry.data.findIndex((l) => l.id === list.id);
    if (idx >= 0) {
      entry.data[idx] = list;
      return;
    }
  }
}

export function invalidateGroceryListsCache(): void {
  cacheInvalidatedAt = Date.now();
  Object.keys(listsByHousehold).forEach((k) => delete listsByHousehold[k]);
  Object.keys(listByIdCache).forEach((k) => delete listByIdCache[k]);
}
